// Phase 2D.2B — the SHAPE of the signup-confirmation landing URL.
//
// Only BOOLEANS are ever produced here. No access token, refresh token,
// authorization code, fragment, e-mail or provider error text is stored,
// exported or logged.
//
// The repository uses the Supabase implicit flow, so a genuine confirmation
// link lands as ``#access_token=...&type=signup`` and a dead link lands as
// ``#error=...&error_code=...``.
//
// ``SIGNUP_LINK`` is captured once at boot, before the Supabase client can strip
// the fragment. ``readSignupLinkState()`` re-reads the CURRENT address so a
// client-side navigation into the callback route is judged correctly too.
//
// Neither is ever enough to start onboarding: that takes a genuine Supabase Auth
// session. The worst either can do is show a refusal or keep the screen waiting.

function readParams() {
  if (typeof window === "undefined" || !window.location) return new URLSearchParams();
  const fragment = String(window.location.hash || "").replace(/^#/, "");
  const query = String(window.location.search || "").replace(/^\?/, "");
  return new URLSearchParams([query, fragment].filter(Boolean).join("&"));
}

export function readSignupLinkState() {
  const params = readParams();
  const type = params.get("type");
  const errorLabel = `${params.get("error_code") || ""} ${params.get("error") || ""}`;
  return {
    // A confirmation-shaped address: the SDK is expected to establish a real
    // session from it.
    looksLikeSignupConfirmation:
      (type === "signup" || type === "email") && Boolean(params.get("access_token")),
    // The provider refused the link (expired, already used, malformed).
    failed: Boolean(
      params.get("error") || params.get("error_code") || params.get("error_description"),
    ),
    // Boolean only — the provider's own wording is never kept or shown.
    expired: /expired/i.test(errorLabel),
  };
}

export const SIGNUP_LINK = Object.freeze(readSignupLinkState());

export function isSignupCallback() {
  return SIGNUP_LINK.looksLikeSignupConfirmation || readSignupLinkState().looksLikeSignupConfirmation;
}

export function signupLinkFailed() {
  return SIGNUP_LINK.failed || readSignupLinkState().failed;
}

export function signupLinkExpired() {
  return SIGNUP_LINK.expired || readSignupLinkState().expired;
}
